var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/async.ts
async function asyncFilter(arr, filter) {
  const filtered = Symbol("FILTERED");
  const promises = arr.map(async (item) => await filter(item) ? item : filtered);
  return (await Promise.all(promises)).filter((i) => i !== filtered);
}
__name(asyncFilter, "asyncFilter");
function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
__name(sleep, "sleep");

// src/coalesce-with-null.ts
function coalesceWithNull(value) {
  return value === void 0 ? null : value;
}
__name(coalesceWithNull, "coalesceWithNull");

// src/coalesce-with-undefined.ts
function coalesceWithUndefined(value) {
  return value === null ? void 0 : value;
}
__name(coalesceWithUndefined, "coalesceWithUndefined");

// src/duration.ts
var UNITS = [
  "years",
  "months",
  "days",
  "hours",
  "minutes",
  "seconds"
];
function renderValueWithPlurality(pluralisedUnit, value) {
  const singularisedUnit = pluralisedUnit.slice(0, -1);
  return `${value} ${value === 1 ? singularisedUnit : pluralisedUnit}`;
}
__name(renderValueWithPlurality, "renderValueWithPlurality");
function isNegative(duration) {
  return duration.as("milliseconds") < 0;
}
__name(isNegative, "isNegative");
function absoluteDuration(duration) {
  if (isNegative(duration)) {
    return duration.negate();
  }
  return duration;
}
__name(absoluteDuration, "absoluteDuration");
function formatDuration(duration, maxUnit = "days") {
  if (!duration.isValid) {
    throw new Error(`Invalid duration. ${duration.invalidExplanation}`);
  }
  const maxUnitAndSmallerUnits = UNITS.slice(UNITS.indexOf(maxUnit));
  const representation = Object.entries(absoluteDuration(duration).shiftTo(...maxUnitAndSmallerUnits).normalize().toObject()).map(([unit, value]) => [
    unit,
    Math.floor(value)
  ]).filter(([, value]) => value > 0).map(([unit, value]) => renderValueWithPlurality(unit, value)).join(", ");
  const prefix = isNegative(duration) ? "-" : "";
  return prefix.concat(representation);
}
__name(formatDuration, "formatDuration");

// src/http.ts
var OBFUSCATED_HEADERS = [
  "authorization",
  "x-api-key"
];
function obfuscateHeaders(headers) {
  return Object.entries(headers).reduce((obfuscatedHeaders, [key, value]) => ({
    ...obfuscatedHeaders,
    [key]: OBFUSCATED_HEADERS.includes(key.toLowerCase()) && typeof value === "string" ? `${value.slice(0, 10)}...${value.slice(-4)}` : value
  }), headers);
}
__name(obfuscateHeaders, "obfuscateHeaders");
function obfuscateBody(body) {
  function obfuscatePartnerSecret(secretValue) {
    return secretValue.length < 5 ? `${secretValue.slice(0, 1)}...${secretValue.slice(-1)}` : `${secretValue.slice(0, 5)}...${secretValue.slice(-5)}`;
  }
  __name(obfuscatePartnerSecret, "obfuscatePartnerSecret");
  if (typeof body === "object") {
    return Object.entries(body).reduce((obfuscatedBody, [key, value]) => {
      if (key === "synchronizationSettings" && value && typeof value === "object" && "secretValue" in value && value.secretValue) {
        return {
          ...obfuscatedBody,
          [key]: {
            ...value,
            secretValue: obfuscatePartnerSecret(value.secretValue)
          }
        };
      }
      return {
        ...obfuscatedBody,
        [key]: value
      };
    }, body);
  }
  return body;
}
__name(obfuscateBody, "obfuscateBody");

// src/math.ts
import Decimal from "decimal.js";
function roundPrice(price, numberOfDigits = 2) {
  return price.mul(Decimal.pow(10, numberOfDigits)).round().div(Decimal.pow(10, numberOfDigits));
}
__name(roundPrice, "roundPrice");
function ceilPrice(price, numberOfDigits = 2) {
  return price.toDecimalPlaces(Math.max(numberOfDigits + 1, 0), Decimal.ROUND_FLOOR).mul(Decimal.pow(10, numberOfDigits)).ceil().div(Decimal.pow(10, numberOfDigits));
}
__name(ceilPrice, "ceilPrice");
function floorPrice(price, numberOfDigits = 2) {
  return price.mul(Decimal.pow(10, numberOfDigits)).floor().div(Decimal.pow(10, numberOfDigits));
}
__name(floorPrice, "floorPrice");
function clamp(lowerBoundary, value, upperBoundary) {
  let result = value;
  if (lowerBoundary !== null) {
    result = Decimal.max(value, lowerBoundary);
  }
  if (upperBoundary !== null) {
    result = Decimal.min(result, upperBoundary);
  }
  return result;
}
__name(clamp, "clamp");

// src/redact-string.ts
function calculateSegments(totalLength) {
  const maskLength = Math.floor(totalLength * (50 / 100));
  const visibleLength = totalLength - maskLength;
  const startVisibleLength = Math.ceil(visibleLength / 2);
  const endVisibleLength = visibleLength - startVisibleLength;
  return {
    startVisibleLength,
    maskLength,
    endVisibleLength
  };
}
__name(calculateSegments, "calculateSegments");
function calculateExplicitSegments(totalLength, options) {
  const startVisibleLength = Math.min(options.startVisibleLength, totalLength);
  const endVisibleLength = Math.min(options.endVisibleLength, totalLength - startVisibleLength);
  return {
    startVisibleLength,
    endVisibleLength,
    maskLength: totalLength - startVisibleLength - endVisibleLength
  };
}
__name(calculateExplicitSegments, "calculateExplicitSegments");
function redactString(str, options) {
  if (!str) {
    return str;
  }
  if (str.length <= 4) {
    return "*".repeat(str.length);
  }
  const totalLength = str.length;
  const { startVisibleLength, maskLength, endVisibleLength } = options ? calculateExplicitSegments(totalLength, options) : calculateSegments(totalLength);
  const start = str.slice(0, startVisibleLength);
  const mask = "*".repeat(maskLength);
  const end = endVisibleLength > 0 ? str.slice(totalLength - endVisibleLength) : "";
  return `${start}${mask}${end}`;
}
__name(redactString, "redactString");

// src/rename-property.ts
import _ from "lodash";
function renameProperty(oldKey, newKey) {
  return (obj) => {
    const objectWithoutProperty = _.omit(obj, oldKey);
    return _.set(objectWithoutProperty, newKey, obj[oldKey]);
  };
}
__name(renameProperty, "renameProperty");

// src/sort.ts
import { DateTime, FixedOffsetZone } from "luxon";
function dateTimeAscendingSorter(left, right) {
  if (left.isValid === false || right.isValid === false) {
    throw new Error("Unable to compare invalid dates.");
  }
  return left.toMillis() - right.toMillis();
}
__name(dateTimeAscendingSorter, "dateTimeAscendingSorter");
function dateTimeDescendingSorter(left, right) {
  return dateTimeAscendingSorter(right, left);
}
__name(dateTimeDescendingSorter, "dateTimeDescendingSorter");
function dateTimeSorter(key, direction) {
  const sorter = direction === "asc" ? dateTimeAscendingSorter : dateTimeDescendingSorter;
  return (left, right) => sorter(left[key], right[key]);
}
__name(dateTimeSorter, "dateTimeSorter");
function jsDateAscendingSorter(left, right) {
  return dateTimeAscendingSorter(DateTime.fromJSDate(left, {
    zone: FixedOffsetZone.instance(left.getTimezoneOffset())
  }), DateTime.fromJSDate(right, {
    zone: FixedOffsetZone.instance(right.getTimezoneOffset())
  }));
}
__name(jsDateAscendingSorter, "jsDateAscendingSorter");
function numberAscendingSorter(left, right) {
  return left - right;
}
__name(numberAscendingSorter, "numberAscendingSorter");
function numberDescendingSorter(left, right) {
  return right - left;
}
__name(numberDescendingSorter, "numberDescendingSorter");
function stringAscendingSorter(left, right) {
  if (left < right) {
    return -1;
  }
  if (right < left) {
    return 1;
  }
  return 0;
}
__name(stringAscendingSorter, "stringAscendingSorter");
function flightDepartureDateSorter(currentFlight, nextFlight) {
  const currentFlightDepartureDate = DateTime.isDateTime(currentFlight.departureDate) ? currentFlight.departureDate : DateTime.fromISO(currentFlight.departureDate);
  const nextFlightDepartureDate = DateTime.isDateTime(nextFlight.departureDate) ? nextFlight.departureDate : DateTime.fromISO(nextFlight.departureDate);
  if (!currentFlightDepartureDate.isValid || !nextFlightDepartureDate.isValid) {
    throw new Error("Unable to compare the departure date of the flights");
  }
  return currentFlightDepartureDate.toMillis() - nextFlightDepartureDate.toMillis();
}
__name(flightDepartureDateSorter, "flightDepartureDateSorter");
function segmentDepartureDateSorter(currentSegment, nextSegment) {
  const currentSegmentDepartureDate = DateTime.isDateTime(currentSegment.departureStop.date) ? currentSegment.departureStop.date : DateTime.fromISO(currentSegment.departureStop.date);
  const nextSegmentDepartureDate = DateTime.isDateTime(nextSegment.departureStop.date) ? nextSegment.departureStop.date : DateTime.fromISO(nextSegment.departureStop.date);
  if (!currentSegmentDepartureDate.isValid || !nextSegmentDepartureDate.isValid) {
    throw new Error("Unable to compare the departure date of the segments");
  }
  return currentSegmentDepartureDate.toMillis() - nextSegmentDepartureDate.toMillis();
}
__name(segmentDepartureDateSorter, "segmentDepartureDateSorter");

// src/stripe.ts
import Decimal2 from "decimal.js";
var StripePaymentIntentSources = {
  PAYMENT_RETRY: "PAYMENT_RETRY",
  PURCHASE_FUNNEL: "PURCHASE_FUNNEL"
};
var STRIPE_ZERO_DECIMAL_CURRENCIES = [
  "BIF",
  "CLP",
  "DJF",
  "GNF",
  "JPY",
  "KMF",
  "KRW",
  "MGA",
  "PYG",
  "RWF",
  "UGX",
  "VND",
  "VUV",
  "XAF",
  "XOF",
  "XPF",
  "ISK",
  "HUF",
  "TWD"
];
function formatStripeAmount({ amount, currency }) {
  if (currency.toUpperCase() === "ISK") {
    return Decimal2.mul(Math.round(amount), 100).toNumber();
  }
  return STRIPE_ZERO_DECIMAL_CURRENCIES.includes(currency.toUpperCase()) ? amount : Decimal2.mul(amount, 100).toNumber();
}
__name(formatStripeAmount, "formatStripeAmount");
function parseStripeAmount({ amount, currency }) {
  const upperCurrency = currency.toUpperCase();
  if (STRIPE_ZERO_DECIMAL_CURRENCIES.includes(upperCurrency)) {
    return amount;
  }
  return new Decimal2(amount).dividedBy(100).toNumber();
}
__name(parseStripeAmount, "parseStripeAmount");
export {
  STRIPE_ZERO_DECIMAL_CURRENCIES,
  StripePaymentIntentSources,
  absoluteDuration,
  asyncFilter,
  ceilPrice,
  clamp,
  coalesceWithNull,
  coalesceWithUndefined,
  dateTimeAscendingSorter,
  dateTimeDescendingSorter,
  dateTimeSorter,
  flightDepartureDateSorter,
  floorPrice,
  formatDuration,
  formatStripeAmount,
  isNegative,
  jsDateAscendingSorter,
  numberAscendingSorter,
  numberDescendingSorter,
  obfuscateBody,
  obfuscateHeaders,
  parseStripeAmount,
  redactString,
  renameProperty,
  roundPrice,
  segmentDepartureDateSorter,
  sleep,
  stringAscendingSorter
};
//# sourceMappingURL=index.js.map